'use client';

import { useEffect, useRef } from 'react';

export default function WebGLBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', { alpha: true });
    if (!gl) return;

    const vertex = `
      attribute vec2 position;
      varying vec2 vUv;
      void main() {
        vUv = position * 0.5 + 0.5;
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragment = `
      precision highp float;
      uniform float uTime;
      uniform float uScroll;
      varying vec2 vUv;

      float band(vec2 uv, float speed, float thickness) {
        float x = uv.x * 4.0 + uTime * speed + uScroll;
        return smoothstep(thickness, 0.0, abs(sin(x + uv.y * 8.0)));
      }

      void main() {
        vec2 uv = vUv;
        uv.y -= 0.08;

        float b1 = band(uv, 0.6, 0.26);
        float b2 = band(uv + 0.25, 0.35, 0.40);
        float b3 = band(uv + 0.45, 0.2, 0.46);

        vec3 color =
          vec3(0.2, 1.0, 0.8) * b1 +
          vec3(0.6, 0.4, 1.0) * b2 +
          vec3(0.2, 0.8, 0.4) * b3;

        gl_FragColor = vec4(color, 0.45);
      }
    `;

    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type)!;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      return shader;
    };

    const program = gl.createProgram()!;
    gl.attachShader(program, compile(gl.VERTEX_SHADER, vertex));
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragment));
    gl.linkProgram(program);
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );

    const pos = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

    const uTime = gl.getUniformLocation(program, 'uTime');
    const uScroll = gl.getUniformLocation(program, 'uScroll');

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    resize();
    window.addEventListener('resize', resize);

    const start = performance.now();

    const render = () => {
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform1f(uTime, (performance.now() - start) * 0.001);
      gl.uniform1f(uScroll, window.scrollY * 0.0002);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      requestAnimationFrame(render);
    };

    render();

    return () => window.removeEventListener('resize', resize);
  }, []);

  return (
    <canvas data-component-id="src/app/landing-page/components/WebGLBackground.tsx:110:4" data-component-path="src/app/landing-page/components/WebGLBackground.tsx" data-component-line="110" data-component-end-line="113" data-component-file="WebGLBackground.tsx" data-component-name="canvas" data-component-content="%7B%22elementName%22%3A%22canvas%22%2C%22className%22%3A%22fixed%20inset-0%20z-10%20pointer-events-none%22%7D"
      ref={canvasRef}
      className="fixed inset-0 z-10 pointer-events-none"
    />
  );
}
